import React, { useCallback, useEffect } from 'react'

import { Col, Hr, Spacer } from '@amzn/stencil-react-components/layout'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { MediaManager } from 'src/components/MediaContentsEditor/MediaManager'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { Change } from 'src/hooks/DTOEditor'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { ContentItemDTO } from 'src/models/dto/items/ContentItemDTO'
import { Medias } from 'src/models/dto/items/MediaDTO'
import { ModuleLayout } from 'src/models/dto/ModuleDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import {
    ItemEditorTextArea,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { ItemEntityService } from 'src/services/EntityServices/ItemEntityService'
import { ContentHandler } from 'src/services/EntityServices/ItemUpdateHandlers/ContentHandler'

export const ContentItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    moduleLayout,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<ContentItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    useEffect(() => {
        if (moduleLayout !== ModuleLayout.VerticalEvaluation)
            ContentHandler.updateTitle(itemDTO.id, locale, '')
    }, [itemDTO.id, locale, moduleLayout])

    const setLocaleWiseMediaOpt = useCallback(
        (change: Change<Medias | undefined>) => {
            ItemEntityService.updateLocaleWiseMediaOpt(itemId, change)
        },
        [itemId]
    )

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <ItemEditorBasicInfo
                handler={ContentHandler}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
            />
            {moduleLayout === ModuleLayout.VerticalEvaluation && (
                <>
                    <Spacer height={'S200'} />
                    <ResponsiveRow gridGap='S300' alignItems={'flex-start'}>
                        <Col width={'100%'}>
                            <ItemEditorTextInput
                                inputId={'title'}
                                value={itemDTO.titleI18N ? itemDTO.titleI18N[locale] ?? '' : ''}
                                placeholder={'An optional title for the item'}
                                disabled={editDisabled}
                                labelText={'Title'}
                                itemId={itemDTO.id}
                                setValue={(nextValue: string) => {
                                    ContentHandler.updateTitle(itemDTO.id, locale, nextValue)
                                }}
                            />
                        </Col>
                    </ResponsiveRow>
                </>
            )}
            <Spacer height={'S200'} />
            <ResponsiveRow gridGap='S300' alignItems={'flex-start'} flexWrap={'wrap'}>
                <Col flex='1'>
                    <ItemEditorTextArea
                        inputId={'text'}
                        dataTestId={'content-text-editor'}
                        locale={locale}
                        value={itemDTO.textI18N[locale] ?? ''}
                        disabled={editDisabled}
                        itemId={itemDTO.id}
                        setValue={(nextValue: string) => {
                            ContentHandler.updateText(itemDTO.id, locale, nextValue)
                        }}
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.textI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        labelText={'Text'}
                    />
                </Col>
            </ResponsiveRow>
            <Spacer height='S400' />
            <Hr />
            <MediaManager
                itemId={itemDTO.id}
                locale={locale}
                localeWiseMediaOpt={itemDTO.localeWiseMedia}
                setLocaleWiseMediaOpt={setLocaleWiseMediaOpt}
                disabled={editDisabled}
            />
        </>
    )
}
